// apps/web/src/components/security/SecurityManager.tsx
// Security management component

import { useState, useEffect } from 'react';
import type { SecuritySetting } from '../../types/security';
import { apiClient } from '../../lib/apiClient';

export function SecurityManager() {
  const [securitySettings, setSecuritySettings] = useState<SecuritySetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSecuritySettings = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/security');
        setSecuritySettings(response.data.securitySettings);
      } catch (err) {
        setError('Failed to load security settings');
        console.error('Error loading security settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSecuritySettings();
  }, []);

  if (loading) {
    return <div className="loading">Loading security settings...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="security-manager">
      <div className="header">
        <h2>Security Management</h2>
      </div>

      <div className="security-grid">
        {securitySettings.map(setting => (
          <SecurityCard key={setting.id} setting={setting} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function SecurityCard({ setting }: { setting: SecuritySetting }) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ENABLED': return 'status-enabled';
      case 'DISABLED': return 'status-disabled';
      case 'SECURE': return 'status-secure';
      case 'INSECURE': return 'status-insecure';
      default: return 'status-default';
    }
  };

  return (
    <div className="security-card">
      <div className="security-header">
        <h3>{setting.name}</h3>
        <span className={`status-badge ${getStatusColor(setting.status)}`}>
          {setting.status}
        </span>
      </div>
      <p>{setting.description || 'No description'}</p>
      <p><strong>Type:</strong> {setting.settingType}</p>
      <p><strong>Last Updated:</strong> {new Date(setting.updatedAt).toLocaleDateString()}</p>
      <div className="actions">
        <button className="btn btn-secondary">View Details</button>
        <button className="btn btn-warning">Configure</button>
      </div>
    </div>
  );
}